/**
 * WAITING — practice mode while waiting for opponents to fill the room.
 *
 * UI surface:
 *   - profile cards visible, "shooting" mode (no betting framing)
 *   - no instruction banner, no countdown overlay, no lock-in button
 *   - camera at default anchor
 *   - practice dice on the table (3 for cee-lo, 2 for craps) so the
 *     player can fidget while waiting; cleared on STARTING entry
 *
 * Updates only need to refresh the player roster (joins / leaves) and
 * keep the dice-result cache empty (a leftover from a previous round
 * shouldn't linger into a new game). No betting / roll / animation
 * concerns live in this state.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class WaitingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    // Visible UI baseline for waiting: profiles in shooting mode, every
    // other overlay hidden, lock-in cleared.
    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.instructionBanner?.hide();
    game.countdownOverlay?.stop();
    game.bettingHUD3D?.setLockInVisible(false);

    // Camera back to default — covers the case where the previous round
    // ended with the camera at a betting / zoom anchor.
    game.cameraController?.setMode('default');

    // Practice dice. Skip recreate when the count already matches so
    // late joiners don't see a flash of dice replacement.
    const practiceCount = update.gameType === 'craps' ? 2 : 3;
    if (game.diceController) {
      const haveCount = game.diceController.getDice().length;
      if (haveCount !== practiceCount) {
        game.diceController.createDice(this.practicePositions(practiceCount));
      }
      game.cameraController?.setDice(game.diceController.getDice());
    }

    // Fresh round → clear dice-result dedup so any leftover icons
    // recompute (typically clears them, since `player.result` is null
    // during WAITING).
    game.clearResultDedup();
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // The only thing that changes during WAITING is the player roster
    // (join / leave / balance). Refresh profiles + reconcile any dice
    // result the cache hasn't seen yet (e.g. very-late joiner where
    // server still has player.result populated from before reset).
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    // Clear the practice dice now that warm-up is over. The next state
    // (STARTING / HI_LO / SHOOTER_SET_STACK) doesn't need them on the
    // table; HI_LO and roll states spawn their own dice via the
    // dice-authorize flow.
    const game = this.game;
    game.diceController?.clearDice();
    game.cameraController?.setDice([]);
  }

  private practicePositions(count: number): Array<{ x: number; y: number; z: number }> {
    if (count === 2) {
      return [
        { x: -0.75, y: 1, z: 5 },
        { x: 0.75, y: 1, z: 5 },
      ];
    }
    return [
      { x: -1.5, y: 1, z: 5 },
      { x: 0, y: 1, z: 5 },
      { x: 1.5, y: 1, z: 5 },
    ];
  }
}
